import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import classNames from 'classnames';

import { useFormFieldControl } from '@/components/atoms/FormField/FormFieldContext';
import styles from '@/components/atoms/FormField/formControl.module.scss';

// `prefix` is an RDFa attribute on every HTML element; here it is the start adornment.
export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
    /** Marks the field invalid. Inside a FormField an `error` does this already. */
    invalid?: boolean;
    /** Joined to the start of the input, such as an icon that says what goes in it. */
    prefix?: ReactNode;
    /** Joined to the end of the input, such as a search icon or a unit. */
    suffix?: ReactNode;
    /** For the box holding the input and its adornments; only rendered with one of them. */
    groupClassName?: string;
    /** For each adornment's box. */
    adornmentClassName?: string;
    ref?: Ref<HTMLInputElement>;
}

function isShown(node: ReactNode): boolean {
    return node !== undefined && node !== null && node !== false && node !== '';
}

/**
 * A single-line input: text, number, email, password, search, time and the other text-like
 * types. Every input attribute passes through, and the ref reaches the `<input>`, so
 * react-hook-form's `register` spreads straight onto it. `className` always styles the
 * `<input>`, with or without adornments.
 */
export default function TextField(props: TextFieldProps) {
    const {
        ref,
        className,
        id,
        invalid,
        type = 'text',
        prefix,
        suffix,
        groupClassName,
        adornmentClassName,
        'aria-describedby': ariaDescribedBy,
        ...inputProps
    } = props;
    const accessible = useFormFieldControl({
        id,
        invalid,
        'aria-describedby': ariaDescribedBy,
    });

    const input = (
        <input
            ref={ref}
            type={type}
            {...accessible}
            {...inputProps}
            className={classNames(styles.control, className)}
        />
    );

    const hasPrefix = isShown(prefix);
    const hasSuffix = isShown(suffix);
    if (!hasPrefix && !hasSuffix) return input;

    return (
        <div className={classNames(styles.group, groupClassName)}>
            {hasPrefix && (
                <span className={classNames(styles.adornment, styles.prefix, adornmentClassName)}>
                    {prefix}
                </span>
            )}
            {input}
            {hasSuffix && (
                <span className={classNames(styles.adornment, styles.suffix, adornmentClassName)}>
                    {suffix}
                </span>
            )}
        </div>
    );
}
